/**
 * R2 gallery photo storage client.
 * Uses Supabase Edge Functions for presigned upload URLs and R2 deletes.
 * Base URL: VITE_R2_PUBLIC_URL (e.g. https://media.kconnect.pl)
 */

import { supabase } from '@/lib/supabase';
import { logger } from '@/lib/logger';

const R2_PUBLIC_BASE = (import.meta.env.VITE_R2_PUBLIC_URL || '').replace(/\/$/, '');

/** Normalize MIME type for R2 presigned URL (mobile often sends empty or with charset). */
function normalizeContentType(type) {
  const raw = (type || 'image/jpeg').trim().toLowerCase().split(';')[0]?.trim() || 'image/jpeg';
  const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
  return allowed.includes(raw) ? raw : 'image/jpeg';
}

/**
 * Fetch presigned upload URL and public URL for a gallery photo path.
 * Returns contentType so the client can send the exact header that was signed (avoids 403 on mobile).
 * @param {string} path - Storage path (e.g. photos/{branchId}/{groupId}/{id}.jpg)
 * @param {string} [contentType='image/jpeg'] - Will be normalized; use file.type from client.
 * @returns {Promise<{ uploadUrl: string, publicUrl: string, contentType: string }>}
 */
export async function getPresignedUploadUrl(path, contentType = 'image/jpeg') {
  const normalized = normalizeContentType(contentType);
  const { data, error } = await supabase.functions.invoke('r2-presigned-upload', {
    body: { path, contentType: normalized }
  });
  if (error) {
    logger.error('r2-presigned-upload error', { error });
    throw error;
  }
  if (data?.error) {
    throw new Error(data.error);
  }
  if (!data?.uploadUrl || !data?.publicUrl) {
    throw new Error('Invalid response from r2-presigned-upload');
  }
  return {
    uploadUrl: data.uploadUrl,
    publicUrl: data.publicUrl,
    contentType: data.contentType || normalized
  };
}

/**
 * Upload file to R2 via presigned PUT URL.
 * Uses mode: 'cors' and credentials: 'omit' so the request is explicit and works from any origin (R2 CORS must allow your app origin).
 * @param {string} uploadUrl - From getPresignedUploadUrl
 * @param {File} file
 * @param {string} contentType - Exact value returned from getPresignedUploadUrl
 */
export async function uploadPhotoViaPresignedUrl(uploadUrl, file, contentType) {
  const header = contentType || normalizeContentType(file?.type);
  let res;
  try {
    res = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      mode: 'cors',
      credentials: 'omit',
      referrerPolicy: 'no-referrer',
      headers: { 'Content-Type': header }
    });
  } catch (err) {
    logger.error('R2 PUT request failed (network/CORS?)', { message: err?.message, name: err?.name });
    throw new Error(
      'Upload failed: network or CORS. Ensure R2 bucket CORS AllowedOrigins includes this site’s origin (e.g. https://app.kconnect.pl).'
    );
  }
  if (!res.ok) {
    const t = await res.text();
    logger.error('R2 PUT failed', { status: res.status, body: t });
    const snippet = t?.length ? (t.length <= 400 ? t : t.slice(0, 400) + '…') : null;
    throw new Error(snippet ? `Upload failed: ${res.status} - ${snippet}` : `Upload failed: ${res.status}`);
  }
}

/**
 * Upload photo to R2 via presigned URL only (no proxy).
 * R2 bucket CORS AllowedOrigins must include your app origin (e.g. https://app.kconnect.pl, http://localhost:5173).
 * @param {string} path - Storage path (e.g. photos/branchId/groupId/name.jpg)
 * @param {File} file
 * @param {string} [contentType='image/jpeg']
 * @returns {Promise<string>} publicUrl
 */
export async function uploadPhotoToR2(path, file, contentType = 'image/jpeg') {
  const ct = contentType || normalizeContentType(file?.type);
  const { uploadUrl, publicUrl, contentType: _ct } = await getPresignedUploadUrl(path, ct);
  await uploadPhotoViaPresignedUrl(uploadUrl, file, _ct);
  return publicUrl;
}

/**
 * Build public URL for a gallery photo path.
 * @param {string} path
 * @returns {string}
 */
export function getPublicUrl(path) {
  if (!R2_PUBLIC_BASE) return '';
  return `${R2_PUBLIC_BASE}/${path}`;
}

/**
 * Delete gallery photo from R2 via Edge Function. Caller must delete DB row separately.
 * @param {string} photoId
 */
export async function deletePhotoFromR2(photoId) {
  const { data, error } = await supabase.functions.invoke('r2-delete-photo', {
    body: { photoId }
  });
  if (error) {
    logger.error('r2-delete-photo error', { error });
    throw error;
  }
  if (data?.error) {
    throw new Error(data.error);
  }
  return data;
}
